import { ArrowUpRight } from "lucide-react";
import { Reveal, Overline } from "@/components/Kinetic";
import { useLang } from "@/i18n/LanguageContext";

export const CERNIO_STEPS_EN = [
  {
    index: "01",
    title: "Understand the product",
    text: "Cernio starts from the product itself: category, use case, price position and the customers it is built for.",
  },
  {
    index: "02",
    title: "Identify the right channel",
    text: "It maps who would realistically sell or buy the product — distributors, importers, dealers, wholesalers or direct buyers.",
  },
  {
    index: "03",
    title: "Explore target markets",
    text: "Countries and regions are assessed for fit: demand signals, market structure and competitive presence.",
  },
  {
    index: "04",
    title: "Discover relevant companies",
    text: "Within each market, Cernio surfaces companies whose actual business matches the product and the channel.",
  },
  {
    index: "05",
    title: "Evaluate opportunities",
    text: "Candidate companies come with relevance context and reasoning, giving export teams a structured shortlist to review and investigate further.",
  },
];

export const CERNIO_STEPS_TR = [
  {
    index: "01",
    title: "Ürünü anlama",
    text: "Cernio, ürünün kendisinden başlar: kategori, kullanım alanı, fiyat konumu ve ürünün hedeflediği müşteriler.",
  },
  {
    index: "02",
    title: "Doğru kanalı belirleme",
    text: "Ürünü gerçekçi olarak kimin satacağını veya satın alacağını haritalar — distribütörler, ithalatçılar, bayiler, toptancılar veya doğrudan alıcılar.",
  },
  {
    index: "03",
    title: "Hedef pazarları keşfetme",
    text: "Ülkeler ve bölgeler uygunluk açısından değerlendirilir: talep sinyalleri, pazar yapısı ve rekabet durumu.",
  },
  {
    index: "04",
    title: "İlgili şirketleri keşfetme",
    text: "Her pazarda Cernio, işi ürün ve kanalla gerçekten örtüşen şirketleri ortaya çıkarır.",
  },
  {
    index: "05",
    title: "Fırsatları değerlendirme",
    text: "Aday şirketler, uygunluk gerekçeleri ve bağlamlarıyla birlikte sunulur. İhracat ekipleri bu kısa listeyi inceleyerek araştırmayı sürdürebilir.",
  },
];

export default function CernioFeature() {
  const { lang, tx } = useLang();
  const steps = lang === "tr" ? CERNIO_STEPS_TR : CERNIO_STEPS_EN;

  return (
    <section id="cernio" className="bg-white py-20 sm:py-28 lg:py-32">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-16">
          <div className="lg:col-span-5">
            <Reveal>
              <Overline>{tx("Flagship product", "Amiral gemisi ürün")}</Overline>
            </Reveal>
            <Reveal delay={0.08}>
              <h2 className="mt-6 font-display text-4xl font-bold tracking-tight text-ink sm:text-5xl">
                Cernio
              </h2>
            </Reveal>
            <Reveal delay={0.16}>
              <p className="mt-5 text-lg leading-relaxed text-slate-700">
                {tx(
                  "AI-powered B2B market and channel intelligence for exporters.",
                  "İhracatçılar için yapay zekâ destekli B2B pazar ve kanal istihbaratı.",
                )}
              </p>
            </Reveal>
            <Reveal delay={0.24}>
              <p className="mt-5 max-w-md text-base leading-relaxed text-slate-600">
                {tx(
                  "Cernio grew from a practical problem: B2B research produces company names, but relevance is harder to establish. GADULABS combines software engineering, AI and business context to connect the product, its possible channels and the companies worth reviewing.",
                  "Cernio somut bir problemden doğdu: B2B araştırmasında şirket isimlerine ulaşmak kolay, uygunluğu anlamak daha zordur. GADULABS; ürün, olası satış kanalları ve incelenmeye değer şirketler arasında bağ kurmak için yazılım mühendisliğini, yapay zekâyı ve iş bilgisini birleştirir.",
                )}
              </p>
            </Reveal>
            <Reveal delay={0.32} className="mt-9">
              <a
                href="https://getcernio.com"
                data-testid="cernio-feature-cta"
                className="focus-ring group inline-flex items-center gap-2 bg-navy px-6 py-3.5 text-sm font-medium text-white transition-colors duration-300 hover:bg-ink"
              >
                {tx("Discover Cernio", "Cernio'yu Keşfedin")}
                <ArrowUpRight
                  size={16}
                  className="transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
                />
              </a>
            </Reveal>
          </div>

          <div className="lg:col-span-7">
            <div className="border-t border-slate-200">
              {steps.map((s, i) => (
                <Reveal key={s.index} delay={i * 0.08} y={20}>
                  <div
                    data-testid={`cernio-flow-step-${i + 1}`}
                    className="group grid grid-cols-[auto_1fr] gap-5 border-b border-slate-200 py-6 transition-colors duration-300 sm:gap-8"
                  >
                    <span className="pt-0.5 font-mono text-xs tracking-[0.15em] text-slate-400 transition-colors duration-300 group-hover:text-gold">
                      {s.index}
                    </span>
                    <div>
                      <h3 className="font-display text-lg font-bold tracking-tight text-ink sm:text-xl">
                        {s.title}
                      </h3>
                      <p className="mt-2 max-w-lg text-sm leading-relaxed text-slate-600">
                        {s.text}
                      </p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
